import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import type { CommandBarAction, ActionContext } from '../actions/types'

/**
 * Derive the current action context from the URL pathname
 */
function deriveContext(pathname: string): ActionContext {
  if (pathname.startsWith('/settings')) {
    return 'settings-view'
  }
  if (pathname.includes('/calendar')) {
    return 'calendar-view'
  }
  if (pathname.includes('/analytics')) {
    return 'analytics-view'
  }
  if (pathname.startsWith('/comments') || pathname.includes('/community')) {
    return 'community-view'
  }
  if (pathname.startsWith('/channels/')) {
    return 'channel-view'
  }
  if (pathname.startsWith('/drafts') || pathname.includes('/draft')) {
    return 'draft-view'
  }
  return 'global'
}

/**
 * Options for the useContextActions hook.
 */
interface UseContextActionsOptions {
  /** Override the current context (useful for testing) */
  contextOverride?: ActionContext
}

/**
 * Hook that filters actions based on the current page context.
 *
 * Actions can specify which contexts they should appear in. This hook
 * filters the action list to only show relevant actions for the current page.
 */
export function useContextActions(
  actions: CommandBarAction[],
  options: UseContextActionsOptions = {},
): CommandBarAction[] {
  const location = useLocation()
  const { contextOverride } = options

  const filteredActions = useMemo(() => {
    const currentContext = contextOverride ?? deriveContext(location.pathname)

    return actions.filter((action) => {
      // If no context specified, show everywhere
      if (!action.context || action.context.length === 0) {
        return true
      }

      // Show if 'global' is in the context list
      if (action.context.includes('global')) {
        return true
      }

      // Show if current context matches
      return action.context.includes(currentContext)
    })
  }, [actions, location.pathname, contextOverride])

  return filteredActions
}

/**
 * Get the current action context
 */
export function useCurrentContext(): ActionContext {
  const location = useLocation()
  return deriveContext(location.pathname)
}
